import React, { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const PAIR = "BTCUSDT";
const POLL_MS = 15000;
const MAX_POINTS = 24;

async function fetchBinanceLastPrice() {
  const res = await fetch(
    `https://api.binance.com/api/v3/ticker/price?symbol=${PAIR}`,
  );
  if (!res.ok) throw new Error("Binance price failed");
  const data = await res.json();
  return Number(data.price);
}

async function fetchBybitLastPrice() {
  const res = await fetch(
    `https://api.bybit.com/v5/market/tickers?category=spot&symbol=${PAIR}`,
  );
  if (!res.ok) throw new Error("Bybit price failed");
  const data = await res.json();
  const list = data?.result?.list;
  const row = Array.isArray(list) && list[0] ? list[0] : null;
  if (!row?.lastPrice) throw new Error("Bybit parse failed");
  return Number(row.lastPrice);
}

const LiveExchangePrices = () => {
  const [series, setSeries] = useState([]);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const mounted = useRef(true);

  const tick = async () => {
    try {
      const [binance, bybit] = await Promise.all([
        fetchBinanceLastPrice(),
        fetchBybitLastPrice(),
      ]);
      if (!mounted.current) return;
      const t = new Date();
      setError("");
      setLastUpdate(t);
      setSeries((prev) => {
        const next = [
          ...prev,
          {
            t: t.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            }),
            binance: Number(binance.toFixed(2)),
            bybit: Number(bybit.toFixed(2)),
          },
        ];
        return next.length > MAX_POINTS ? next.slice(-MAX_POINTS) : next;
      });
    } catch (e) {
      if (mounted.current) setError(e?.message || "Unable to load prices");
    }
  };

  useEffect(() => {
    mounted.current = true;
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--text-primary)] opacity-70">
        <span>
          Spot {PAIR.replace("USDT", "/USDT")} — public ticker poll (
          {POLL_MS / 1000}s)
        </span>
        {lastUpdate ? (
          <span>Last update: {lastUpdate.toLocaleTimeString()}</span>
        ) : null}
      </div>
      {error ? (
        <p className="text-sm text-amber-500">{error}</p>
      ) : null}
      <div className="h-[220px] w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
            <XAxis
              dataKey="t"
              stroke="#A1A1AA"
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke="#A1A1AA"
              tick={{ fontSize: 10 }}
              domain={["auto", "auto"]}
              tickFormatter={(v) => `$${v}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#161A1D",
                border: "1px solid #22c55e",
                borderRadius: "12px",
              }}
              formatter={(value, name) => [`$${Number(value).toFixed(2)}`, name]}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="binance"
              name="Binance"
              stroke="#22c55e"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="bybit"
              name="Bybit"
              stroke="#38bdf8"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LiveExchangePrices;
